import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { ScheduleFields, SendRecurrenceFrequency } from 'domains/campaigns/types';
import { getRecurrenceLabel } from 'domains/campaigns/utils';
import DropdownButton from 'domains/core/components/DropdownButton';
import InfoSnackbar from 'domains/core/components/Snackbars/InfoSnackbar';
import InputLabel from 'domains/core/components/InputLabel';
import { getRecommendedTime } from './utils/getRecommendedTime';
import { disabledMessage } from './RecommendedTimeSwitch';
import theme from 'theme';

const dropdownButtonStyles = {
    backgroundColor: theme.palette.action.active,
    textTransform: 'capitalize',
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
    },
};

export type Props = {
    scheduleFields: ScheduleFields;
    sendDate: DateTime;
    editSchedule: (payload: Partial<ScheduleFields>) => void;
    setEndDate: (payload: DateTime) => void;
    setHasEndDate: (payload: boolean) => void;
    setRecommendedTimeSwitchTouched: (payload: boolean) => void;
    setSendTime: (payload: DateTime) => void;
};

const RecurrenceDropdown = ({
    scheduleFields,
    sendDate,
    editSchedule,
    setEndDate,
    setHasEndDate,
    setRecommendedTimeSwitchTouched,
    setSendTime,
}: Props) => {
    const { isRecommendedTimeEnabled, recurrenceFrequency } = scheduleFields;

    const handleDoesNotRepeatSelection = () => {
        const recurrence = SendRecurrenceFrequency.DOES_NOT_REPEAT;
        const newDatetime = getRecommendedTime(sendDate, recurrence);

        if (isRecommendedTimeEnabled) {
            setSendTime(newDatetime);
        }

        editSchedule({
            ...scheduleFields,
            recurrenceFrequency: recurrence,
            scheduledEndTimestamp: '',
            ...(isRecommendedTimeEnabled && { scheduledTimestamp: newDatetime.toISO() }),
        });

        setHasEndDate(false);
        setEndDate(null);
        setRecommendedTimeSwitchTouched(true);
    };

    const handleContinuousSelection = () => {
        editSchedule({
            ...scheduleFields,
            recurrenceFrequency: SendRecurrenceFrequency.CONTINUOUS,
            isRecommendedTimeEnabled: false,
        });

        setRecommendedTimeSwitchTouched(true);
    };

    const optionsRecurrenceFrequency = [
        {
            label: getRecurrenceLabel(SendRecurrenceFrequency.DOES_NOT_REPEAT).toLowerCase(),
            onClick: handleDoesNotRepeatSelection,
        },
        {
            label: (
                <InputLabel
                    label="Continual"
                    labelIconTooltipText="Continual sends are delivered when the audience criteria is met."
                />
            ),
            onClick: handleContinuousSelection,
        },
        {
            label: getRecurrenceLabel(SendRecurrenceFrequency.DAILY).toLowerCase(),
            onClick: () => handleRecurrenceSelection(SendRecurrenceFrequency.DAILY),
        },
        {
            label: getRecurrenceLabel(SendRecurrenceFrequency.WEEKLY, sendDate?.toISO()).toLowerCase(),
            onClick: () => handleRecurrenceSelection(SendRecurrenceFrequency.WEEKLY),
        },
        {
            label: getRecurrenceLabel(SendRecurrenceFrequency.EVERY_WEEKDAY).toLowerCase(),
            onClick: () => handleRecurrenceSelection(SendRecurrenceFrequency.EVERY_WEEKDAY),
        },
    ];

    const handleRecurrenceSelection = (recurrence: SendRecurrenceFrequency) => {
        const newDatetime = getRecommendedTime(sendDate, recurrence);

        if (isRecommendedTimeEnabled) {
            setSendTime(newDatetime);
        }

        editSchedule({
            ...scheduleFields,
            recurrenceFrequency: recurrence,
            ...(isRecommendedTimeEnabled && { scheduledTimestamp: newDatetime.toISO() }),
        });

        setRecommendedTimeSwitchTouched(true);
    };

    const renderRecommendedTimeSnackbar = () => {
        if (!isRecommendedTimeEnabled || recurrenceFrequency === SendRecurrenceFrequency.DOES_NOT_REPEAT) return;

        if (recurrenceFrequency === SendRecurrenceFrequency.CONTINUOUS) {
            return (
                <InfoSnackbar
                    key="continuous-recurrence-recommended-time-disabled"
                    successMessage={
                        <Typography color="info.content" component="span" variant="body2">
                            {disabledMessage}
                        </Typography>
                    }
                />
            );
        }

        return (
            <InfoSnackbar
                key={`${recurrenceFrequency}-recurrence-recommended-time-updated`}
                successMessage={
                    <Typography color="info.content" component="span" variant="body2">
                        Cured's recommended time updated based on{' '}
                        <Typography color="info.dark" component="span" variant="body2">
                            recurring send
                        </Typography>
                        .
                    </Typography>
                }
            />
        );
    };

    return (
        <>
            {renderRecommendedTimeSnackbar()}
            <Box>
                <DropdownButton
                    sx={dropdownButtonStyles}
                    data-testid="recurrence-button"
                    label={getRecurrenceLabel(recurrenceFrequency, sendDate.toISO())}
                    options={optionsRecurrenceFrequency}
                    variant="contained"
                    size="small"
                />
            </Box>
        </>
    );
};

export default RecurrenceDropdown;
